"use client";

import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { Loader2, Palette, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  backendErrorMessage,
  useCameraWhiteBalance,
  useSetWhiteBalance,
} from "./api";
import { useLocalization } from "@/context/localization-context";

const GainControl = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="text-sm">{label}</Label>
      <Input
        type="number"
        min={0.1}
        max={8}
        step={0.05}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-7 w-24 text-right"
      />
    </div>
    <Slider
      min={0.1}
      max={8}
      step={0.05}
      value={[value]}
      onValueChange={([v]) => onChange(v)}
    />
  </div>
);

export const WhiteBalanceCard = ({ deviceId }: { deviceId: string }) => {
  const { t, formatNumber } = useLocalization();
  const whiteBalance = useCameraWhiteBalance(deviceId);
  const setWhiteBalance = useSetWhiteBalance(deviceId);

  const [mode, setMode] = useState<"auto" | "manual">("auto");
  const [redGain, setRedGain] = useState(2.0);
  const [blueGain, setBlueGain] = useState(1.8);

  useEffect(() => {
    if (!whiteBalance.data) return;
    setMode(whiteBalance.data.awb_mode);
    if (whiteBalance.data.awb_red_gain != null) {
      setRedGain(Number(whiteBalance.data.awb_red_gain.toFixed(2)));
    }
    if (whiteBalance.data.awb_blue_gain != null) {
      setBlueGain(Number(whiteBalance.data.awb_blue_gain.toFixed(2)));
    }
  }, [whiteBalance.data]);

  const formatGain = (value: number | null) =>
    value != null
      ? formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "?";

  const apply = async () => {
    try {
      await setWhiteBalance.mutateAsync(
        mode === "auto"
          ? { mode: "auto" }
          : { mode: "manual", red_gain: redGain, blue_gain: blueGain },
      );
      toast.success(t("cameras.whiteBalance.applied"));
    } catch (error) {
      toast.error(
        t("cameras.whiteBalance.failed", { error: backendErrorMessage(error) }),
      );
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette className="h-4 w-4" />
            {t("cameras.whiteBalance.title")}
          </CardTitle>
          <div className="flex items-center gap-2">
            {whiteBalance.data && (
              <Badge variant="outline" className="font-mono text-xs">
                {t("cameras.whiteBalance.liveValues", {
                  mode:
                    whiteBalance.data.awb_mode === "auto"
                      ? t("cameras.control.modeAuto")
                      : whiteBalance.data.awb_mode === "manual"
                        ? t("cameras.control.modeManual")
                        : whiteBalance.data.awb_mode,
                  red: formatGain(whiteBalance.data.awb_red_gain),
                  blue: formatGain(whiteBalance.data.awb_blue_gain),
                })}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => whiteBalance.refetch()}
              disabled={whiteBalance.isFetching}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${whiteBalance.isFetching ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={mode}
          onValueChange={(value) => value && setMode(value as "auto" | "manual")}
          className="justify-start"
        >
          <ToggleGroupItem value="auto">{t("cameras.control.modeAuto")}</ToggleGroupItem>
          <ToggleGroupItem value="manual">{t("cameras.control.modeManual")}</ToggleGroupItem>
        </ToggleGroup>

        {mode === "manual" && (
          <div className="space-y-4">
            <GainControl
              label={t("cameras.whiteBalance.redGain")}
              value={redGain}
              onChange={setRedGain}
            />
            <GainControl
              label={t("cameras.whiteBalance.blueGain")}
              value={blueGain}
              onChange={setBlueGain}
            />
          </div>
        )}

        <Button
          size="sm"
          onClick={apply}
          disabled={setWhiteBalance.isPending}
          className="w-full"
        >
          {setWhiteBalance.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {t("cameras.whiteBalance.apply")}
        </Button>
      </CardContent>
    </Card>
  );
};
